import MinimumOrderAndMultiplesOfInformation from "@bevco/Components/MinimumOrderAndMultiplesOfInformation";
import AddedToCartCheckCircle from "@bevco/MobiusOverrides/Icons/AddedToCartCheckCircle";
import InfoCircle from "@bevco/MobiusOverrides/Icons/InfoCircle";
import ProductAvailability, { ProductAvailabilityStyles } from "@bevco/Overrides/Components/ProductAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@bevco/Overrides/Components/ProductPartNumbers";
import setAddedToCartModalOpen, {
    OrderLinePartNumbersDisplay,
} from "@bevco/Store/Handlers/Custom/CustomSetAddedToCartModal";
import useCustomQuoteRequired from "@bevco/Store/Handlers/Utilities/useCustomQuoteRequired";
import { CustomState } from "@bevco/Store/Reducers";
import { useWidgetCartLineBadges } from "@bevco/Widgets/VerndaleBox/CartLineBadges";
import { buttonCommon } from "@bevco/Utilities/styleguide";
import { modal as modalStyleguide } from "@bevco/Utilities/styleguide/Modal";
import { colorStrings, mediaQueriesBP } from "@bevco/Utilities/variables";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    BadgeTypeValues,
    CartLineModel,
    ProductModel,
} from "@insite/client-framework/Types/ApiModels";
import ProductBrand from "@insite/content-library/Components/ProductBrand";
import ProductDescription, {
    ProductDescriptionStyles,
} from "@insite/content-library/Components/ProductDescription";
import ProductImage from "@insite/content-library/Components/ProductImage";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import isMobile from "ismobilejs";

interface OwnProps {
    extendedStyles?: AddedToCartModalStyles;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

const mapStateToProps = (state: CustomState) => {
    const cart = getCurrentCartState(state).value;
    return {
        isOpen: state.customAddedToCartModal.isOpen,
        cartLine: state.customAddedToCartModal.cartLine,
        originalQtyOrdered: state.customAddedToCartModal.originalQtyOrdered,
        existingQtyOrdered: state.customAddedToCartModal.existingQtyOrdered,
        isBulkAdd: state.customAddedToCartModal.isBulkAdd,
        bulkProductCount: state.customAddedToCartModal.bulkProductCount,
        isQtyAdjusted: state.customAddedToCartModal.isQtyAdjusted,
        product: state.customAddedToCartModal.product,
        productInfo: state.customAddedToCartModal.productInfo,
        hasSpoolProducts: state.customAddedToCartModal.hasSpoolProducts,
        spoolNoticeId: state.customAddedToCartModal.spoolNoticeId,
        hideViewCartButton: state.customAddedToCartModal.hideViewCartButton,
        forceQuoteRequired: state.customAddedToCartModal.forceQuoteRequired,
        orderLinePartNumbers: state.customAddedToCartModal.orderLinePartNumbers,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        lineCount: cart?.lineCount ?? 0,
        orderSubTotalDisplay: cart?.orderSubTotalDisplay ?? "",
        failedToGetRealTimeInventory: cart?.failedToGetRealTimeInventory ?? false,
    };
};

const mapDispatchToProps = {
    setAddedToCartModalOpen,
};

export interface AddedToCartModalStyles {
    modal?: ModalPresentationProps;
    headerWrapper?: InjectableCss;
    headerIcon?: InjectableCss;
    headerTitle?: TypographyPresentationProps;
    infoMessageWrapper?: InjectableCss;
    infoMessageText?: TypographyPresentationProps;
    bulkMessageText?: TypographyPresentationProps;
    spoolMessageText?: TypographyPresentationProps;
    productDetailsWrapper?: InjectableCss;
    productDetailsContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productContentGridItem?: GridItemProps;
    productContentContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    productPricingGridItem?: GridItemProps;
    pricingRow?: InjectableCss;
    pricingHeadingAndText?: SmallHeadingAndTextStyles;
    totalsWrapper?: InjectableCss;
    totalRow?: InjectableCss;
    totalLabel?: TypographyPresentationProps;
    totalValue?: TypographyPresentationProps;
    actionsWrapper?: InjectableCss;
    continueButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    qtyStrikethrough?: TypographyPresentationProps;
    qtyAdjusted?: TypographyPresentationProps;
    pricingLabel?: TypographyPresentationProps;
    pricingValue?: TypographyPresentationProps;
    productAvailability?: ProductAvailabilityStyles;
    productInfoTotalsGridItem?: GridItemProps;
    totalSubtotalLabel?: TypographyPresentationProps;
    totalSubtotalValue?: TypographyPresentationProps;
    orderLinePartNumber?: TypographyPresentationProps;
}

export const addedToCartModalStyles: AddedToCartModalStyles = {
    modal: {
        size: 700,
        cssOverrides: {
            ...modalStyleguide.defaultProps?.cssOverrides,
            modalContent: css`
                ${modalStyleguide.defaultProps?.cssOverrides?.modalContent}
                background: ${getColor(colorStrings.commonGlobalWhite)};
                padding: 24px !important;
            `,
            modalTitle: css`
                padding: 16px 24px !important;
                border-bottom: 1px solid ${getColor(colorStrings.commonDisabled)} !important;
                background: ${getColor(colorStrings.commonGlobalWhite)};
            `,
        },
        closeButtonProps: {
            ...modalStyleguide.defaultProps?.closeButtonProps,
        },
        closeButtonIconProps: {
            ...modalStyleguide.defaultProps?.closeButtonIconProps,
            css: css`
                color: ${getColor(colorStrings.commonGlobalBlack)} !important;
            `,
        },
    },
    headerWrapper: {
        css: css`
            align-items: center;
            display: flex;
            gap: 16px;
        `,
    },
    headerIcon: {
        css: css`
            align-items: center;
            display: flex;
            flex-shrink: 0;
            height: 28px;
            justify-content: center;
            width: 28px;

            svg {
                height: 28px;
                width: 28px;
            }
        `,
    },
    headerTitle: {
        variant: "h3",
        weight: "bold",
        css: css`
            margin: 0;
        `,
    },
    infoMessageWrapper: {
        css: css`
            align-items: flex-start;
            background-color: #eef6fc;
            border: 1px solid ${getColor("info.main")};
            display: flex;
            gap: 10px;
            margin-bottom: 20px;
            padding: 12px 16px;
        `,
    },
    infoMessageText: {
        variant: "p",
        color: "info.main",
        css: css`
            margin: 0;
            font-size: 12px !important;
            font-style: normal !important;
            font-weight: 400 !important;
            line-height: 16px !important;
        `,
    },
    bulkMessageText: {
        variant: "p",
        css: css`
            margin: 0 0 20px;
            text-align: center;
        `,
    },
    spoolMessageText: {
        variant: "p",
        css: css`
            margin: 0 0 20px;
            text-align: center;
            width: 70%;
            margin-left: auto;
            margin-right: auto;
        `,
    },
    productDetailsWrapper: {
        css: css`
            border: 1px solid ${getColor(colorStrings.commonGlobalGray04)};
            margin-bottom: 20px;
            padding: 16px;
        `,
    },
    productDetailsContainer: {
        gap: 16,
    },
    productImageGridItem: {
        width: [3, 3, 2, 2, 2],
    },
    productContentGridItem: {
        width: [9, 9, 10, 10, 10],
    },
    productContentContainer: {
        gap: 16,
    },
    productInfoGridItem: {
        width: [12, 12, 7, 7, 7],
        css: css`
            flex-direction: column;
            gap: 4px;
        `,
    },
    productDescription: {
        productDetailDescription: {
            css: css`
                font-size: 16px;
                font-style: normal;
                font-weight: 400;
                line-height: 20px;
            `,
        },
    },
    productPartNumbers: {
        container: {
            gap: 4,
        },
        manufacturerItemGridItem: {
            css: css`
               font-size: 14px !important;
               font-style: normal !important;
               line-height: normal !important;
            `,
        },
        erpNumberGridItem: {
            css: css`
               font-size: 14px !important;
               font-style: normal !important;
               line-height: normal !important;
            `,
        }
    },
    productAvailability: {
        messageGridItem: {
            css: css`
                gap: 4px;

                ${IconWrapper} {
                    height: 20px;
                    margin-right: 5px !important;
                    width: 20px;
                }

                ${IconWrapper} svg {
                    height: 20px;
                    width: 20px;
                }
            `,
        },
        messageText: {
            css: css`
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
            `,
        },
        messageTableGridItem: {
            css: css`
                gap: 4px;
                flex-direction: column;

                ${IconWrapper} {
                    height: 20px;
                    margin-right: 5px !important;
                    width: 20px;
                }

                ${IconWrapper} svg {
                    height: 20px;
                    width: 20px;
                }
            `,
        },
    },
    productPricingGridItem: {
        width: [12, 12, 5, 5, 5],
        css: css`
        flex-direction: column;
        gap: 4px;
            ${mediaQueriesBP.minLG} {
                text-align: right;
            }
        `,
    },
    productInfoTotalsGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            gap: 4px;
        `,
    },
    pricingRow: {
        css: css`
            display: flex;
            height: 20px;
            justify-content: space-between;
            align-items: flex-start;
            align-self: stretch;
        `,
    },
    pricingHeadingAndText: {
        wrapper: {
            css: css`
                display: flex;
                height: 20px;
                justify-content: space-between;
                align-items: flex-start;
                align-self: stretch;
                flex-direction: row;
            `,
        },
        heading: {
            css: css`
                font-size: 14px !important;
                font-style: normal !important;
                font-weight: 700 !important;
                line-height: normal !important;
                white-space: nowrap;
            `,
        },
        text: {
            css: css`
                font-size: 14px !important;
                font-style: normal !important;
                font-weight: 400 !important;
                line-height: normal !important;
                width: auto !important;
                white-space: nowrap;
            `,
        },
    },
    totalsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-top: 5px;
            width: 100%;
        `,
    },
    totalRow: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    totalLabel: {
        variant: "p",
        css: css`
            font-size: 16px !important;
            font-style: normal !important;
            font-weight: 700 !important;
            line-height: 20px !important;
            margin-bottom: 0 !important;
        `,
    },
    totalValue: {
        variant: "p",
        weight: "bold",
        css: css`
            font-size: 16px !important;
            font-style: normal !important;
            font-weight: 700 !important;
            line-height: 20px !important;
            margin-bottom: 0 !important;
        `,
    },
    totalSubtotalLabel: {
        css: css`
            font-size: 16px !important;
            font-style: normal !important;
            font-weight: 400 !important;
            line-height: 20px !important;
        `,
    },
    totalSubtotalValue: {
        css: css`
            font-size: 16px !important;
            font-style: normal !important;
            font-weight: 400 !important;
            line-height: 20px !important;
            margin-bottom: 0 !important;
        `,
    },
    orderLinePartNumber: {
        css: css`
            font-size: 14px !important;
            font-style: normal !important;
            font-weight: 400 !important;
            line-height: normal !important;
            margin: 0;

            strong {
                font-weight: 700;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
        `,
    },
    continueButton: {
        variant: "secondary",
        css: css`
            width: 100%;
        `,
    },
    viewCartButton: {
        variant: "primary",
        css: css`
            width: 100%;
        `,
    },
    qtyStrikethrough: {
        variant: "p",
        css: css`
            color: ${getColor(colorStrings.commonGlobalGray01)} !important;
            display: inline;
            margin: 0 0 0 6px;
            font-size: 14px !important;
            font-style: normal !important;
            font-weight: 400 !important;
            line-height: normal !important;
            white-space: nowrap;

            &&& {
                text-decoration: line-through;
                text-decoration-color: ${getColor(colorStrings.primaryMain)};
                text-decoration-thickness: 3px;
            }
        `,
    },
    qtyAdjusted: {
        variant: "p",
        weight: "bold",
        css: css`
            display: inline;
            margin: 0;
            font-size: 14px !important;
            font-style: normal !important;
            font-weight: 400 !important;
            line-height: normal !important;
            white-space: nowrap;
        `,
    },
    pricingLabel: {
        variant: "legend",
        css: css`
            font-size: 14px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;      
            padding-inline-start: 0 !important;
            white-space: nowrap;
        `,
    },
    pricingValue: {
        variant: "p",
        css: css`
            margin: 0;
        `,
    },
};

interface ProductDetailsProps {
    cartLine: CartLineModel;
    originalQtyOrdered?: number;
    existingQtyOrdered?: number;
    isQtyAdjusted?: boolean;
    product?: ProductModel;
    productInfo?: ProductInfo;
    failedToGetRealTimeInventory: boolean;
    lineCount: number;
    orderSubTotalDisplay: string;
    onNavigateToProduct: (event?: React.MouseEvent) => void;
    onNavigateToBrand: () => void;
    styles: AddedToCartModalStyles;
    /** From ProductAddToCartButton "Request a Quote" qty validation link — overrides all quote logic. */
    forceQuoteRequired?: boolean;
    /** Present only when opened from OrderLinesList; drives list-matching part number display. */
    orderLinePartNumbers?: OrderLinePartNumbersDisplay;
}

const AddedToCartModalProductDetails = ({
    cartLine,
    originalQtyOrdered,
    existingQtyOrdered,
    isQtyAdjusted,
    product,
    productInfo,
    failedToGetRealTimeInventory,
    lineCount,
    orderSubTotalDisplay,
    onNavigateToProduct,
    onNavigateToBrand,
    styles,
    forceQuoteRequired,
    orderLinePartNumbers,
}: ProductDetailsProps) => {
    // Match PDP ProductAddToCartButton / useCustomQuoteRequired so modal availability
    // stays consistent with what the user saw on the product page.
    let quoteRequired = useCustomQuoteRequired(product && productInfo ? { product, productInfo } : cartLine);

    const { textBadges: cartLineTextBadges } = useWidgetCartLineBadges({
        cartLine,
        showTextBadges: true,
        showImageBadges: false,
    });
    const productTextBadges =
        product?.badges?.filter(
            badge => badge.displayOnBadgeWidget && badge.badgeType === BadgeTypeValues.Text && badge.displayText,
        ) ?? null;
    const textBadges =
        cartLineTextBadges && cartLineTextBadges.length > 0 ? cartLineTextBadges : productTextBadges;

    const isCmiProduct = `${cartLine.properties["isc_isCmiProduct"] ?? cartLine.properties["isCmiProduct"] ?? product?.properties["isCmiProduct"] ?? ""}`.toLowerCase() ===
        "true";
    const manufacturerNameValue =
        cartLine.properties["manufacturerNameValue"] ?? product?.properties["manufacturerNameValue"] ?? "";
    const brand = cartLine.brand ?? product?.brand;
    const shouldShowBrand =
        !!brand && !isCmiProduct && manufacturerNameValue.toLowerCase() !== "approved manufacturer";
    const showManufacturerName = !isCmiProduct && !!manufacturerNameValue;
    const shouldShowManufacturerItem =
        !isCmiProduct && manufacturerNameValue.toLowerCase() !== "approved manufacturer";
    const allowSpoolOptions =
        `${cartLine.properties["allowSpoolOptions"] ?? product?.properties?.["allowSpoolOptions"] ?? ""}`.toLowerCase() ===
        "true";
    const hasSpools =
        allowSpoolOptions &&
        !!cartLine.properties["spoolSelected"] &&
        !!cartLine.properties["spoolLength"];
    const unitOfMeasureDisplay = cartLine.unitOfMeasureDisplay || cartLine.unitOfMeasure;

    const minimumOrderQty = +(
        cartLine.properties["product_minimumOrderQty"] ??
        product?.minimumOrderQty ??
        product?.properties["ecommMOQ"] ??
        product?.properties["minimumOrderQty"] ??
        "0"
    );
    const multiplesOfQty = +(
        cartLine.properties["product_multipleSalesQty"] ??
        product?.detail?.multipleSaleQty ??
        product?.properties["incrementalEcommMOQ"] ??
        product?.properties["multipleSaleQty"] ??
        "0"
    );
    const moqUom =
        cartLine.properties["baseUnitOfMeasure"] ??
        product?.unitOfMeasures?.[0]?.unitOfMeasureDisplay ??
        unitOfMeasureDisplay;

    const unitPriceDisplay =
        cartLine.pricing?.unitNetPriceDisplay && unitOfMeasureDisplay
            ? `${cartLine.pricing.unitNetPriceDisplay}/${unitOfMeasureDisplay}`
            : cartLine.pricing?.unitNetPriceDisplay ?? "";
    let adjustedQtyOrdered = cartLine.qtyOrdered ?? 0;
    const potentialMultiplesAdjusted =
        multiplesOfQty > 1 && originalQtyOrdered !== undefined
            ? Math.ceil(originalQtyOrdered / multiplesOfQty) * multiplesOfQty
            : originalQtyOrdered;
    // Prefer qty from before this add (AddToCart overwrites cartLine.qtyOrdered with the entered qty).
    const existingQtyBeforeAdd = existingQtyOrdered ?? 0;
    const moqAlreadyCovered = minimumOrderQty > 1 && existingQtyBeforeAdd >= minimumOrderQty;

    if (isQtyAdjusted && originalQtyOrdered !== undefined) {
        if (moqAlreadyCovered && potentialMultiplesAdjusted !== undefined) {
            // MOQ already met: only round the add qty up to multiples (50→100, 150→200, 250→300).
            adjustedQtyOrdered = potentialMultiplesAdjusted;
        } else if (minimumOrderQty > 1 && existingQtyBeforeAdd < minimumOrderQty) {
            // Cart empty or line below MOQ: always round up to MOQ (50/100/150 → 500).
            const minimumAdjustedQty = Math.max(originalQtyOrdered, minimumOrderQty);
            adjustedQtyOrdered =
                multiplesOfQty > 1
                    ? Math.ceil(minimumAdjustedQty / multiplesOfQty) * multiplesOfQty
                    : minimumAdjustedQty;
        } else if (adjustedQtyOrdered === originalQtyOrdered && potentialMultiplesAdjusted !== undefined) {
            adjustedQtyOrdered = potentialMultiplesAdjusted;
        }
    }
    const showOriginalQtyOrdered =
        isQtyAdjusted && originalQtyOrdered !== undefined && adjustedQtyOrdered !== originalQtyOrdered;
    const isAdjustedToMultiplesOnly =
        showOriginalQtyOrdered &&
        moqAlreadyCovered &&
        multiplesOfQty > 1 &&
        potentialMultiplesAdjusted !== undefined &&
        adjustedQtyOrdered === potentialMultiplesAdjusted;

    const ProductAvailabilityComponent = ProductAvailability as React.ComponentType<any>;

    const inventoryAvailability =
        productInfo?.inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (productInfo?.unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;

    if (forceQuoteRequired) {
        // ProductAddToCartButton "Request a Quote" from qty validation — always quote required.
        quoteRequired = true;
    } else {
        // Inventory additionalResults.isQuoteRequired is the first rule and overrides
        // quoteRequired from useCustomQuoteRequired / product context above.
        const isQuoteRequiredFromInventory = productInfo?.inventory?.additionalResults?.isQuoteRequired;
        if (isQuoteRequiredFromInventory?.toLowerCase() === "true") {
            quoteRequired = true;
        } else {
            quoteRequired = false;
        }
    }

    // ProductAvailability only renders the Quote Required state when availability has a message
    // (see inventoryTextComponent). Prefer cart line availability for quote products.
    const quoteRequiredAvailabilityFallback: AvailabilityDto = {
        message: translate("Quote Required") as string,
        messageType: AvailabilityMessageType.Available,
        requiresRealTimeInventory: false,
    };
    const availability = quoteRequired
        ? cartLine.availability ?? inventoryAvailability ?? quoteRequiredAvailabilityFallback
        : inventoryAvailability ?? cartLine.availability ?? undefined;

    // Ensure ProductAvailability's useCustomQuoteRequired treats this as quote-required
    // (covers isQuotedByUser / zero-price cases where product.quoteRequired may be false).
    // When forceQuoteRequired, clone inventory.isQuoteRequired so the shared helper
    // (inventory as source of truth) still returns true — do not change useCustomQuoteRequired.
    const productForAvailability: ProductModel | undefined = product
        ? { ...product, quoteRequired }
        : undefined;

    // Order-detail Buy Again can pass product without productInfo if id was missing;
    // synthesize enough ProductInfo so we stay on ProductContext (full properties) instead
    // of falling back to the sparse cart-line property bag.
    const productInfoForAvailability = (() => {
        const baseInfo =
            productInfo ??
            (productForAvailability?.id
                ? {
                      productId: productForAvailability.id,
                      qtyOrdered: cartLine.qtyOrdered ?? 1,
                      unitOfMeasure: cartLine.unitOfMeasure || "",
                      productDetailPath: productForAvailability.canonicalUrl || cartLine.productUri || "",
                  }
                : undefined);

        if (!baseInfo) {
            return undefined;
        }

        if (forceQuoteRequired && baseInfo.inventory) {
            return {
                ...baseInfo,
                inventory: {
                    ...baseInfo.inventory,
                    additionalResults: {
                        ...baseInfo.inventory.additionalResults,
                        isQuoteRequired: "true",
                    },
                },
            };
        }

        return baseInfo;
    })();

    const availabilityElement = productForAvailability && productInfoForAvailability ? (
        <ProductContext.Provider value={{ product: productForAvailability, productInfo: productInfoForAvailability }}>
                <ProductAvailabilityComponent
                    productId={productInfoForAvailability.productId}
                    availability={availability}
                    unitOfMeasure={productInfoForAvailability.unitOfMeasure}
                    trackInventory={productForAvailability?.trackInventory}
                    failedToLoadInventory={failedToGetRealTimeInventory}
                    extendedStyles={styles.productAvailability}
                />
        </ProductContext.Provider>
    ) : (
        <CartLineContext.Provider value={cartLine}>
            <ProductAvailabilityComponent
                productId={cartLine.productId!}
                availability={availability ?? cartLine.availability!}
                unitOfMeasure={cartLine.unitOfMeasure}
                trackInventory={cartLine.trackInventory}
                failedToLoadInventory={failedToGetRealTimeInventory}
                extendedStyles={styles.productAvailability}
            />
        </CartLineContext.Provider>
    );

    return (
        <StyledWrapper {...styles.productDetailsWrapper}>
            <GridContainer {...styles.productDetailsContainer}>
                <GridItem {...styles.productImageGridItem}>
                    <ProductImage product={cartLine} onClick={onNavigateToProduct} />
                </GridItem>
                <GridItem {...styles.productContentGridItem}>
                    <GridContainer {...styles.productContentContainer}>
                        <GridItem {...styles.productInfoGridItem}>
                            {shouldShowBrand && (
                                <StyledWrapper onClick={onNavigateToBrand}>
                                    <ProductBrand brand={brand!} />
                                </StyledWrapper>
                            )}
                            <ProductDescription
                                product={cartLine}
                                onClick={onNavigateToProduct}
                                extendedStyles={styles.productDescription}
                            />
                            {orderLinePartNumbers ? (
                                <>
                                    {orderLinePartNumbers.isCmiProduct &&
                                        orderLinePartNumbers.customerProductNumber && (
                                            <Typography as="p" {...styles.orderLinePartNumber}>
                                                <strong>{translate("Customer Material #")}: </strong>
                                                {orderLinePartNumbers.customerProductNumber}
                                            </Typography>
                                        )}
                                    {!orderLinePartNumbers.isCmiProduct && (
                                        <Typography as="p" {...styles.orderLinePartNumber}>
                                            <strong>{siteMessage("IEWC_Part_#")}: </strong>
                                            {orderLinePartNumbers.iewcPartNumber}
                                        </Typography>
                                    )}
                                    {orderLinePartNumbers.isCmiProduct && (
                                        <Typography as="p" {...styles.orderLinePartNumber}>
                                            <strong>{siteMessage("IEWC_Part_#")}: </strong>
                                            {orderLinePartNumbers.iewcPartNumber}
                                        </Typography>
                                    )}
                                    {orderLinePartNumbers.manufacturerItem &&
                                        !orderLinePartNumbers.isCmiProduct && (
                                            <Typography as="p" {...styles.orderLinePartNumber}>
                                                <strong>{translate("MFG #")}: </strong>
                                                {orderLinePartNumbers.manufacturerItem}
                                            </Typography>
                                        )}
                                </>
                            ) : (
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    showManufacturerItem={shouldShowManufacturerItem}
                                    cmiCustomerNumber={cartLine.properties["isc_cmiCustomer"] ?? product?.properties["cmiCustomer"] ?? ""}
                                    modelNumber={cartLine.properties["product_modelNumber"] ?? product?.detail?.modelNumber ?? ""}
                                    cmiBevcoMaterial={cartLine.properties["isc_cmiBevcoMaterial"] ?? product?.properties["cmiBevcoMaterial"] ?? ""}
                                    manufacturerName={
                                        showManufacturerName ? manufacturerNameValue : ""
                                    }
                                    extendedStyles={styles.productPartNumbers}
                                />
                            )}
                            {textBadges && textBadges.length > 0 && (
                                <ProductTextBadges badgeList={textBadges} maxBadges={1} />
                            )}
                            {availabilityElement}
                        </GridItem>
                        <GridItem {...styles.productPricingGridItem}>
                            <StyledWrapper {...styles.pricingRow}>
                                <Typography {...styles.pricingLabel}>{translate("Quantity")}</Typography>
                                <StyledWrapper>
                                    <Typography {...styles.qtyAdjusted}>
                                        {adjustedQtyOrdered} {unitOfMeasureDisplay}
                                    </Typography>
                                    {showOriginalQtyOrdered && (
                                        <Typography {...styles.qtyStrikethrough}>
                                            {originalQtyOrdered} {unitOfMeasureDisplay}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                            {!quoteRequired && unitPriceDisplay && (
                                <SmallHeadingAndText
                                    heading={translate("Price")}
                                    text={unitPriceDisplay}
                                    extendedStyles={styles.pricingHeadingAndText}
                                />
                            )}
                            {hasSpools && !quoteRequired && (
                                <>
                                    <SmallHeadingAndText
                                        heading={translate("No. of Spools")}
                                        text={cartLine.properties["spoolSelected"]}
                                        extendedStyles={styles.pricingHeadingAndText}
                                    />
                                    <SmallHeadingAndText
                                        heading={translate("Spool Length")}
                                        text={`${cartLine.properties["spoolLength"]} ${unitOfMeasureDisplay}`}
                                        extendedStyles={styles.pricingHeadingAndText}
                                    />
                                </>
                            )}
                            {minimumOrderQty > 1 && (
                                <SmallHeadingAndText
                                    heading={translate("Min. Order")}
                                    text={`${minimumOrderQty} ${moqUom}`}
                                    extendedStyles={styles.pricingHeadingAndText}
                                />
                            )}
                            {multiplesOfQty > 1 && (
                                <SmallHeadingAndText
                                    heading={translate("Multiples of")}
                                    text={`${multiplesOfQty} ${moqUom}`}
                                    extendedStyles={styles.pricingHeadingAndText}
                                />
                            )}
                        </GridItem>
                        {!isMobile(window.navigator).apple.phone && <GridItem {...styles.productInfoTotalsGridItem}>
                            <StyledWrapper {...styles.totalsWrapper}>
                                {!quoteRequired && cartLine.pricing?.extendedUnitNetPriceDisplay && (
                                    <StyledWrapper {...styles.totalRow}>
                                        <Typography {...styles.totalLabel} weight="bold">
                                            {translate(isAdjustedToMultiplesOnly ? "Item total in the cart" : "Item Total")}
                                        </Typography>
                                        <Typography {...styles.totalValue}>
                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.totalRow}>
                                    <Typography {...styles.totalSubtotalLabel}>
                                        {translate("Cart Subtotal ({0} Items)", lineCount.toString())}
                                    </Typography>
                                    <Typography {...styles.totalSubtotalValue}>{orderSubTotalDisplay}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>}
                    </GridContainer>
                </GridItem>
                {isMobile(window.navigator).apple.phone && <GridItem {...styles.productInfoTotalsGridItem}>
                    <StyledWrapper {...styles.totalsWrapper}>
                        {!quoteRequired && cartLine.pricing?.extendedUnitNetPriceDisplay && (
                            <StyledWrapper {...styles.totalRow}>
                                <Typography {...styles.totalLabel} weight="bold">
                                    {translate(isAdjustedToMultiplesOnly ? "Item total in the cart" : "Item Total")}
                                </Typography>
                                <Typography {...styles.totalValue}>
                                    {cartLine.pricing.extendedUnitNetPriceDisplay}
                                </Typography>
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.totalRow}>
                            <Typography {...styles.totalSubtotalLabel}>
                                {translate("Cart Subtotal ({0} Items)", lineCount.toString())}
                            </Typography>
                            <Typography {...styles.totalSubtotalValue}>{orderSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>}
            </GridContainer>
        </StyledWrapper>
    );
};

const AddedToCartModal = ({
    isOpen,
    cartLine,
    originalQtyOrdered,
    existingQtyOrdered,
    isBulkAdd,
    bulkProductCount,
    isQtyAdjusted,
    product,
    productInfo,
    hasSpoolProducts,
    spoolNoticeId,
    hideViewCartButton,
    forceQuoteRequired,
    orderLinePartNumbers,
    cartPageUrl,
    lineCount,
    orderSubTotalDisplay,
    failedToGetRealTimeInventory,
    setAddedToCartModalOpen,
    history,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(addedToCartModalStyles, extendedStyles));

    const modalCloseHandler = () => {
        setAddedToCartModalOpen({ isOpen: false });
    };

    const navigateToProduct = (event?: React.MouseEvent) => {
        event?.preventDefault();
        modalCloseHandler();
        if (cartLine?.productUri) {
            history.push(cartLine.productUri);
        }
    };

    // ProductBrand already links to brand.detailPagePath; only close the modal.
    const navigateToBrand = () => {
        modalCloseHandler();
    };

    const continueShoppingHandler = () => {
        modalCloseHandler();
    };

    const viewCartHandler = () => {
        modalCloseHandler();
        if (cartPageUrl) {
            history.push(cartPageUrl);
        }
    };

    const spoolNoticeMessage =
        spoolNoticeId === "order-already-in-cart"
            ? siteMessage("SpoolModal_ContentMessage_OrderAlreadyInCart", "order")
            : spoolNoticeId === "order-some-items-already-in-cart"
              ? siteMessage("SpoolModal_ContentMessage_OrderSomeItemsAlreadyInCart", "order")
              : spoolNoticeId === "list-already-in-cart"
                ? siteMessage("SpoolModal_ContentMessage_OrderAlreadyInCart", "list")
                : spoolNoticeId === "list-some-items-already-in-cart"
                  ? siteMessage("SpoolModal_ContentMessage_OrderSomeItemsAlreadyInCart", "list")
                  : spoolNoticeId === "order-history"
                    ? siteMessage("SpoolModal_ContentMessage_OrderHistory")
                    : undefined;

    const spoolNoticeTitle =
        spoolNoticeId === "order-already-in-cart"
            ? siteMessage("SpoolModal_TitleMessage_OrderAlreadyInCart", "Order")
            : spoolNoticeId === "order-some-items-already-in-cart"
              ? siteMessage("SpoolModal_TitleMessage_OrderSomeItemsAlreadyInCart", "Order")
              : spoolNoticeId === "list-already-in-cart"
                ? siteMessage("SpoolModal_TitleMessage_OrderAlreadyInCart", "List")
                : spoolNoticeId === "list-some-items-already-in-cart"
                  ? siteMessage("SpoolModal_TitleMessage_OrderSomeItemsAlreadyInCart", "List")
                  : spoolNoticeId === "order-history"
                    ? siteMessage("SpoolModal_TitleMessage_OrderHistory")
                    : undefined;

    const isMessageOnlyView = !!isBulkAdd || !!spoolNoticeMessage;

    const headline = (
        <StyledWrapper {...styles.headerWrapper}>
            <StyledWrapper {...styles.headerIcon}>
                <AddedToCartCheckCircle />
            </StyledWrapper>
            <Typography {...styles.headerTitle}>
                {spoolNoticeTitle ?? translate("Added To Cart")}
            </Typography>
        </StyledWrapper>
    );

    return (
        <Modal
            {...styles.modal}
            headline={headline}
            isOpen={isOpen}
            handleClose={modalCloseHandler}
            closeOnScrimClick={true}
            data-test-selector="addedToCartModal"
        >


            {isMessageOnlyView ? (
                <>
                    {isBulkAdd && (
                        <Typography {...styles.bulkMessageText}>
                            {siteMessage("Cart_AllProductsAddedToCartFromModal", bulkProductCount?.toString() ?? "")}
                        </Typography>
                    )}
                    {spoolNoticeMessage ? (
                        <Typography {...styles.spoolMessageText}>{spoolNoticeMessage}</Typography>
                    ) : (
                        hasSpoolProducts && (
                            <Typography {...styles.spoolMessageText}>
                                {siteMessage("SpoolModal_ContentMessage_OrderHistory")}
                            </Typography>
                        )
                    )}
                </>
            ) : (
                <>
                    {isQtyAdjusted && (
                        <StyledWrapper {...styles.infoMessageWrapper}>
                            <Icon src={InfoCircle} color="info.main" size={18} />
                            <Typography {...styles.infoMessageText}>{siteMessage("Cart_QuantityAdjusted")}</Typography>
                        </StyledWrapper>
                    )}
                    {cartLine && (
                        <AddedToCartModalProductDetails
                            cartLine={cartLine}
                            originalQtyOrdered={originalQtyOrdered}
                            existingQtyOrdered={existingQtyOrdered}
                            isQtyAdjusted={isQtyAdjusted}
                            product={product}
                            productInfo={productInfo}
                            failedToGetRealTimeInventory={failedToGetRealTimeInventory}
                            lineCount={lineCount}
                            orderSubTotalDisplay={orderSubTotalDisplay}
                            onNavigateToProduct={navigateToProduct}
                            onNavigateToBrand={navigateToBrand}
                            styles={styles}
                            forceQuoteRequired={forceQuoteRequired}
                            orderLinePartNumbers={orderLinePartNumbers}
                        />
                    )}
                </>
            )}

            {isBulkAdd && isQtyAdjusted && (
                <StyledWrapper {...styles.infoMessageWrapper}>
                    <Icon src={InfoCircle} color="info.main" size={18} />
                    <Typography {...styles.infoMessageText}>{siteMessage("Cart_QuantityAdjusted")}</Typography>
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.actionsWrapper}>
                <Button {...styles.continueButton} onClick={continueShoppingHandler}>
                    <Typography {...buttonCommon.typographyProps}>{translate("Continue Shopping")}</Typography>
                </Button>
                {!hideViewCartButton && (
                    <Button {...styles.viewCartButton} onClick={viewCartHandler}>
                        <Typography {...buttonCommon.typographyProps}>{translate("View Cart")}</Typography>
                    </Button>
                )}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(AddedToCartModal));
